import { ref, readonly, type Ref } from 'vue';
import type { PlayerProfile, AuthError, AuthUser } from '@/types/auth';
import {
  isPlayerProfile,
  isAuthUser,
  createAuthError,
  isAuthError,
  isAuthErrorBody,
} from '@/types/auth';
import { AUTH_CONFIG, ENV_CONFIG, UI_CONFIG } from '@/config/constants';
import {
  useAuth,
  _updateAuthUser,
  _clearAuthState,
  _getMockUserEmail,
} from '@/composables/useAuth';
import STRINGS from '@/constants/strings';

const useMock = AUTH_CONFIG.isMockAuthEnabled();
if (import.meta.env.MODE === 'production' && useMock) {
  throw new Error('Mock auth must not be enabled in production builds');
}

// Module-level singleton state
const profile = ref<PlayerProfile | null>(null);
const isLoading = ref(false);
const profileError = ref<AuthError | null>(null);

// --- Profile API interface ---

interface ProfileApi {
  fetchProfile(id: string): Promise<PlayerProfile>;
  fetchAccountEmail(): Promise<string>;
  updateName(name: string): Promise<AuthUser>;
  changePassword(currentPassword: string, newPassword: string): Promise<AuthUser>;
  deleteAccount(password: string): Promise<void>;
}

// --- Mock implementation ---

async function mockDelay(): Promise<void> {
  await new Promise(resolve => setTimeout(resolve, AUTH_CONFIG.MOCK_API_DELAY));
}

const mockProfileApi: ProfileApi = {
  async fetchProfile(id: string): Promise<PlayerProfile> {
    await mockDelay();

    const auth = useAuth();
    const user = auth.user.value;
    if (user && user.id === id) {
      return {
        id: user.id,
        name: user.name,
        total_points: 0,
        created_at: new Date().toISOString(),
      };
    }

    throw createAuthError('unknown', 'Player not found');
  },

  async fetchAccountEmail(): Promise<string> {
    await mockDelay();

    const auth = useAuth();
    const user = auth.user.value;
    if (!user) {
      throw createAuthError('invalid_credentials', 'Not authenticated');
    }

    return _getMockUserEmail(user.id) ?? 'user@example.com';
  },

  async updateName(name: string): Promise<AuthUser> {
    await mockDelay();

    const auth = useAuth();
    const user = auth.user.value;
    if (!user) {
      throw createAuthError('invalid_credentials', 'Not authenticated');
    }

    return { id: user.id, name, emailVerified: user.emailVerified };
  },

  async changePassword(_currentPassword: string, _newPassword: string): Promise<AuthUser> {
    await mockDelay();

    const auth = useAuth();
    const user = auth.user.value;
    if (!user) {
      throw createAuthError('invalid_credentials', 'Not authenticated');
    }
    // Mock cannot verify the real password; return the unchanged user so the
    // composable refreshes auth state exactly as the real flow does.
    return { id: user.id, name: user.name, emailVerified: user.emailVerified };
  },

  async deleteAccount(_password: string): Promise<void> {
    await mockDelay();

    const auth = useAuth();
    const user = auth.user.value;
    if (!user) {
      throw createAuthError('invalid_credentials', 'Not authenticated');
    }
    // No-op: the composable handles state clearing after this returns
  },
};

// --- Real implementation ---

const realProfileApi: ProfileApi = {
  async fetchProfile(id: string): Promise<PlayerProfile> {
    const response = await fetch(
      `${ENV_CONFIG.getApiBaseUrl()}/api/profile/${encodeURIComponent(id)}`
    );

    if (response.status === 404) {
      throw createAuthError('unknown', 'Player not found');
    }

    if (!response.ok) {
      throw createAuthError('unknown', `Failed to load profile (status ${response.status})`);
    }

    let json: unknown;
    try {
      json = await response.json();
    } catch {
      throw createAuthError('unknown', 'Server returned an invalid response');
    }

    if (!isPlayerProfile(json)) {
      throw createAuthError('unknown', 'Server returned an invalid response');
    }

    return json;
  },

  async fetchAccountEmail(): Promise<string> {
    const response = await fetch(`${ENV_CONFIG.getApiBaseUrl()}/api/auth/account`, {
      credentials: 'include',
    });

    if (response.status === 401) {
      throw createAuthError('unknown', STRINGS.settingsHub.account.errors.sessionExpired);
    }

    if (!response.ok) {
      throw createAuthError('unknown', `Failed to load email (status ${response.status})`);
    }

    let json: unknown;
    try {
      json = await response.json();
    } catch {
      throw createAuthError('unknown', 'Server returned an invalid response');
    }

    if (
      typeof json !== 'object' ||
      json === null ||
      typeof (json as { email?: unknown }).email !== 'string'
    ) {
      throw createAuthError('unknown', 'Server returned an invalid response');
    }

    return (json as { email: string }).email;
  },

  async updateName(name: string): Promise<AuthUser> {
    const response = await fetch(`${ENV_CONFIG.getApiBaseUrl()}/api/auth/profile/name`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ name }),
    });

    if (response.status === 401) {
      // A 401 here can only mean a dead session (there is no password to be
      // wrong on a name change). Clear the stale local auth state and surface
      // a session-expired message, mirroring the genuine-`unauthorized` branch
      // of `changePassword`. Clearing auth state routes the user back to the
      // login screen via the auth gate. (#290)
      _clearAuthState();
      throw createAuthError('unknown', STRINGS.settingsHub.account.errors.sessionExpired);
    }

    if (response.status === 409) {
      throw createAuthError('name_taken', 'This name is already taken');
    }

    if (response.status === 422) {
      throw createAuthError('unknown', STRINGS.profile.errors.invalidName);
    }

    if (!response.ok) {
      throw createAuthError('unknown', 'Something went wrong. Please try again.');
    }

    let json: unknown;
    try {
      json = await response.json();
    } catch {
      throw createAuthError('unknown', 'Server returned an invalid response');
    }

    if (!isAuthUser(json)) {
      throw createAuthError('unknown', 'Server returned an invalid response');
    }

    return json;
  },

  async changePassword(currentPassword: string, newPassword: string): Promise<AuthUser> {
    const response = await fetch(`${ENV_CONFIG.getApiBaseUrl()}/api/auth/profile/password`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    });

    if (response.status === 401) {
      // A 401 is either a wrong current password (invalid_credentials) or an
      // expired session (unauthorized) — distinguish via the error code so the
      // UI can show the right message.
      let code: string | undefined;
      try {
        const json: unknown = await response.json();
        if (isAuthErrorBody(json)) {
          code = json.code;
        }
      } catch {
        // Body unreadable — treat as a wrong-password failure below.
      }
      if (code === 'unauthorized') {
        // The session is genuinely dead (not a wrong-password failure). Clear
        // the stale local auth state so the UI doesn't keep showing a
        // logged-in user behind the expired-session message.
        _clearAuthState();
        throw createAuthError('unknown', STRINGS.settingsHub.account.errors.sessionExpired);
      }
      throw createAuthError(
        'invalid_credentials',
        STRINGS.settingsHub.account.errors.wrongPassword
      );
    }

    if (response.status === 422) {
      throw createAuthError('validation', STRINGS.settingsHub.account.errors.weakPassword);
    }

    if (!response.ok) {
      throw createAuthError('unknown', 'Something went wrong. Please try again.');
    }

    let json: unknown;
    try {
      json = await response.json();
    } catch {
      throw createAuthError('unknown', 'Server returned an invalid response');
    }

    if (!isAuthUser(json)) {
      throw createAuthError('unknown', 'Server returned an invalid response');
    }

    return json;
  },

  async deleteAccount(password: string): Promise<void> {
    const response = await fetch(`${ENV_CONFIG.getApiBaseUrl()}/api/auth/account`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ password }),
    });

    if (response.status === 401) {
      throw createAuthError('invalid_credentials', 'Incorrect password');
    }

    if (!response.ok) {
      throw createAuthError('unknown', 'Something went wrong. Please try again.');
    }
  },
};

// --- API selection ---

const api: ProfileApi = useMock ? mockProfileApi : realProfileApi;

// --- Validation ---

function validateName(name: string): AuthError | null {
  const trimmed = name.trim();
  if (trimmed.length < UI_CONFIG.VALIDATION.PLAYER_NAME_MIN_LENGTH) {
    return createAuthError(
      'unknown',
      `Name must be at least ${UI_CONFIG.VALIDATION.PLAYER_NAME_MIN_LENGTH} characters`
    );
  }
  if (trimmed.length > UI_CONFIG.VALIDATION.PLAYER_NAME_MAX_LENGTH) {
    return createAuthError(
      'unknown',
      `Name must be at most ${UI_CONFIG.VALIDATION.PLAYER_NAME_MAX_LENGTH} characters`
    );
  }
  if (!UI_CONFIG.VALIDATION.DISPLAY_NAME_PATTERN.test(trimmed)) {
    return createAuthError('unknown', 'Name must contain only letters and numbers');
  }
  return null;
}

// --- Test helper ---

function resetProfileState(): void {
  if (import.meta.env.MODE === 'production') return;
  profile.value = null;
  isLoading.value = false;
  profileError.value = null;
}

// --- Public composable ---

export function useProfile() {
  // Deliberately does NOT touch the shared `isLoading`/`profileError` state:
  // the email row loads independently of the password form, so the caller owns
  // its own loading/error state. Always throws an `AuthError` on failure.
  async function fetchAccountEmail(): Promise<string> {
    try {
      return await api.fetchAccountEmail();
    } catch (err: unknown) {
      if (isAuthError(err)) {
        throw err;
      }
      throw createAuthError(
        'network',
        'Could not connect to the server. Please check your connection.'
      );
    }
  }

  async function fetchProfile(id: string): Promise<void> {
    isLoading.value = true;
    profileError.value = null;

    try {
      const result = await api.fetchProfile(id);
      profile.value = result;
    } catch (err: unknown) {
      if (isAuthError(err)) {
        profileError.value = err;
      } else {
        profileError.value = createAuthError(
          'network',
          'Could not connect to the server. Please check your connection.'
        );
      }
      throw profileError.value;
    } finally {
      isLoading.value = false;
    }
  }

  async function updateName(name: string): Promise<void> {
    const validationError = validateName(name);
    if (validationError) {
      profileError.value = validationError;
      throw validationError;
    }

    isLoading.value = true;
    profileError.value = null;

    try {
      const updatedUser = await api.updateName(name.trim());
      _updateAuthUser(updatedUser);
    } catch (err: unknown) {
      if (isAuthError(err)) {
        profileError.value = err;
      } else {
        profileError.value = createAuthError(
          'network',
          'Could not connect to the server. Please check your connection.'
        );
      }
      throw profileError.value;
    } finally {
      isLoading.value = false;
    }
  }

  async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
    isLoading.value = true;
    profileError.value = null;

    try {
      const updatedUser = await api.changePassword(currentPassword, newPassword);
      // The server invalidates other sessions and mints a fresh one for this
      // device; refresh the local auth user so it stays in sync.
      _updateAuthUser(updatedUser);
    } catch (err: unknown) {
      if (isAuthError(err)) {
        profileError.value = err;
      } else {
        profileError.value = createAuthError(
          'network',
          'Could not connect to the server. Please check your connection.'
        );
      }
      throw profileError.value;
    } finally {
      isLoading.value = false;
    }
  }

  async function deleteAccount(password: string): Promise<void> {
    isLoading.value = true;
    profileError.value = null;

    try {
      await api.deleteAccount(password);
      _clearAuthState();
    } catch (err: unknown) {
      if (isAuthError(err)) {
        profileError.value = err;
      } else {
        profileError.value = createAuthError(
          'network',
          'Could not connect to the server. Please check your connection.'
        );
      }
      throw profileError.value;
    } finally {
      isLoading.value = false;
    }
  }

  function clearError(): void {
    profileError.value = null;
  }

  return {
    profile: readonly(profile) as Readonly<Ref<PlayerProfile | null>>,
    isLoading: readonly(isLoading) as Readonly<Ref<boolean>>,
    error: readonly(profileError) as Readonly<Ref<AuthError | null>>,
    fetchProfile,
    fetchAccountEmail,
    updateName,
    changePassword,
    deleteAccount,
    clearError,
    _resetProfileState: resetProfileState,
  };
}
